import React, { useState, useEffect } from 'react';
import { t, Trans } from '@lingui/macro';
import { HardDrive, FileText, Database, RefreshCw } from 'lucide-react';
import { Workspace } from '../../../models/Workspace';
import { UnifiedStorageReport } from '../../../models/UnifiedStorageReport';
import { Fire } from '../../../services/FireClient';
import { My } from '../../../services/My';
import { UtilApp } from '../../../services/Utils/UtilApp';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Progress } from '../../../components/ui/progress';
import { cn } from '../../../lib/utils';

interface WSAdminStorageProps {
    workspace: Workspace;
}

const WSAdminStorage: React.FC<WSAdminStorageProps> = ({ workspace }) => {
    const [report, setReport] = useState<UnifiedStorageReport>();
    const [inProgress, setInProgress] = useState(false);

    useEffect(() => {
        setInProgress(true);
        const unsub = Fire.ws.subscribeToUnifiedReport(workspace.id, (r) => {
            setReport(r);
            setInProgress(false);
        });
        return () => unsub();
    }, [workspace.id]);

    const getMaxStorageMB = (tier: string | undefined): number => {
        switch (tier) {
            case 'FREE': return 10000;
            case 'BASIC': return 100000;
            case 'STANDARD': return 500000;
            case 'PREMIUM': return 2000000;
            case 'PREMIUM_TRIAL': return 10000;
            default: return 10000;
        }
    };

    const maxMB = getMaxStorageMB(workspace.tier);
    const maxGB = (maxMB / 1000).toFixed(2);
    const poolMaxBytes = maxMB * 1024 * 1024;

    const usedBytes = report?.summary?.totalUsedBytes ?? 0;
    const usedGB = (usedBytes / (1024 ** 3)).toFixed(2);

    const percentUsed = poolMaxBytes > 0 ? (usedBytes / poolMaxBytes) * 100 : 0;

    const formatGB = (bytes: number) => (bytes / (1024 ** 3)).toFixed(2);

    const storageItems = report ? [
        { icon: FileText, label: t`File Storage`, value: formatGB(report.summary.breakdown.fileBytes ?? 0), color: 'text-blue-500' },
        { icon: Database, label: t`Database Storage`, value: formatGB(report.summary.breakdown.firestoreBytes ?? 0), color: 'text-orange-500' }
    ] : [];

    return (
        <div className="w-full max-w-7xl p-4 md:p-6 lg:p-8 space-y-8 bg-white my-6 mx-4 rounded-panel shadow-sm border">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-slate-100 border border-slate-200">
                        <HardDrive className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight"><Trans>Storage</Trans></h2>
                        <p className="text-sm text-muted-foreground"><Trans>Monitor your workspace storage usage.</Trans></p>
                    </div>
                </div>
                {/* Subscription automatically updates, manual refresh not strictly needed but kept if user wants to force-check cloud function later */}
                <Button variant="outline" size="sm" onClick={() => { }} disabled={true}>
                    <RefreshCw className={cn("mr-2 h-4 w-4", inProgress && "animate-spin")} />
                    <Trans>Auto-Sync</Trans>
                </Button>
            </div>

            {/* Overview Card */}
            <Card>
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto mb-4 p-4 rounded-full bg-primary/10 w-fit">
                        <HardDrive className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-3xl font-bold">
                        {usedGB} <span className="text-lg font-normal text-muted-foreground">/ {maxGB} GB</span>
                    </CardTitle>
                    <CardDescription className="mt-2">
                        <Trans>{workspace.name} has used {percentUsed.toFixed(1)}% of its pool.</Trans>
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="w-full max-w-md mx-auto">
                        <Progress value={percentUsed} className="h-3" />
                        <div className="flex justify-between text-xs text-muted-foreground mt-2">
                            <span>{workspace.tier || 'FREE'}</span>
                            <span>{maxGB} GB</span>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Breakdown Card */}
            <Card>
                <CardHeader>
                    <CardTitle><Trans>Storage Breakdown</Trans></CardTitle>
                    <CardDescription><Trans>See how your storage is being used.</Trans></CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {storageItems.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className={cn("p-2 rounded-lg bg-white shadow-sm", item.color)}>
                                        <item.icon className="h-5 w-5" />
                                    </div>
                                    <span className="font-medium">{item.label}</span>
                                </div>
                                <span className="text-muted-foreground font-mono">{item.value} GB</span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Last Updated */}
            {report && (
                <p className="text-center text-sm text-muted-foreground">
                    <Trans>Updated {UtilApp.dateStrToYmd(report.generatedAt.toDate(), My.user.lang)}</Trans>
                </p>
            )}
        </div>
    );
};

export default WSAdminStorage;
